// @flow
import React, { Component } from 'react';
import styled from 'styled-components';
import { colors } from 'bench-style-constants';

import MonthSelector from './MonthSelector';
import Icon from './Icon';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const CalendarYearWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 240px;
  font-size: 14px;
  padding: 8px 12px;
`;

const Year = styled.div`
  font-weight: bold;
  font-size: 14px;
  text-align: center;
  padding: 8px 12px;
`;

const CalendarTitleLayout = styled.div`
  display: grid;
  grid-template-columns: ${({ layout }) => layout || 'auto 1fr auto'};
  align-items: center;
  text-align: center;
  width: 100%;
`;

export const ArrowButton = styled.button`
  width: 20px;
  border: 0;
  background: transparent;
  padding: 0;
  outline: none;

  &:focus {
    box-shadow: 0 0 5px 0 ${colors.water};
  }
`;

const ArrowIcon = styled(Icon)`
  transform: rotate(${({ left }) => (left ? '90' : '-90')}deg);
`;

export type YearMonthRangePickerProps = {
  defaultDisplayedYear: number,
  defaultStartYearMonth?: string,
  defaultEndYearMonth?: string,
  selectionType?: 'single' | 'range',
  calendarType?: 'single' | 'multiple',
  onRangeSelect: (startYearMonth: string | null, endYearMonth: string | null) => void,
};

export type YearMonthRangePickerState = {
  displayedYear: number,
  hoveredYearMonth: string | null,
  startYearMonth: string | null,
  endYearMonth: string | null,
};

class YearMonthRangePicker extends Component<YearMonthRangePickerProps, YearMonthRangePickerState> {
  static defaultProps = {
    defaultStartYearMonth: undefined,
    defaultEndYearMonth: undefined,
    selectionType: 'range',
    calendarType: 'single',
  };

  constructor(props: YearMonthRangePickerProps) {
    super(props);

    this.state = {
      displayedYear: props.defaultDisplayedYear || new Date().getFullYear(),
      hoveredYearMonth: null,
      startYearMonth: props.defaultStartYearMonth || null,
      endYearMonth: props.defaultEndYearMonth || null,
    };
  }

  handleRangeSelectionChange() {
    const { onRangeSelect } = this.props;
    const { startYearMonth, endYearMonth } = this.state;

    onRangeSelect(startYearMonth, endYearMonth);
  }

  updatedDisplayedYear(year: number) {
    this.setState({ displayedYear: year });
  }

  handleYearMonthSelect = (selectedYearMonth: string) => {
    const { selectionType } = this.props;
    let { startYearMonth, endYearMonth } = this.state;

    const isSingleSelection = selectionType === 'single';
    const isRangeSelection = selectionType === 'range';
    const isSingleMonthSelected = startYearMonth === endYearMonth;
    const isRangeSelected = startYearMonth !== endYearMonth;
    const startAndEndEmpty = !startYearMonth && !endYearMonth;

    if (startAndEndEmpty || isRangeSelected || isSingleSelection) {
      startYearMonth = selectedYearMonth;
      endYearMonth = selectedYearMonth;
    }

    if (
      isRangeSelection &&
      isSingleMonthSelected &&
      startYearMonth &&
      selectedYearMonth < startYearMonth
    ) {
      startYearMonth = selectedYearMonth;
    }

    if (
      isRangeSelection &&
      isSingleMonthSelected &&
      startYearMonth &&
      selectedYearMonth > startYearMonth
    ) {
      endYearMonth = selectedYearMonth;
    }

    this.setState(
      {
        startYearMonth,
        endYearMonth,
      },
      this.handleRangeSelectionChange,
    );
  };

  handleYearIncrease = () => {
    this.updatedDisplayedYear(this.state.displayedYear + 1);
  };

  handleYearDecrease = () => {
    this.updatedDisplayedYear(this.state.displayedYear - 1);
  };

  handleMonthHover = (yearMonth: string) => {
    this.setState({ hoveredYearMonth: yearMonth });
  };

  handleMonthUnhover = () => {
    this.setState({ hoveredYearMonth: null });
  };

  render() {
    const { selectionType, calendarType } = this.props;
    const { displayedYear, hoveredYearMonth, startYearMonth, endYearMonth } = this.state;

    return (
      <Wrapper>
        <CalendarYearWrapper>
          <CalendarTitleLayout layout={calendarType === 'multiple' && 'auto 1fr'}>
            <ArrowButton
              data-test-id="year-previous"
              onClick={this.handleYearDecrease}
              type="button"
            >
              <ArrowIcon left icon="caret" size="small" />
            </ArrowButton>
            <Year>{calendarType === 'single' ? displayedYear : displayedYear - 1}</Year>
            {calendarType === 'single' ? (
              <ArrowButton data-test-id="year-next" onClick={this.handleYearIncrease} type="button">
                <ArrowIcon icon="caret" size="small" />
              </ArrowButton>
            ) : (
              <div />
            )}
          </CalendarTitleLayout>
          <MonthSelector
            year={calendarType === 'single' ? displayedYear : displayedYear - 1}
            selectRange={selectionType === 'range'}
            selectedStartYearMonth={startYearMonth}
            selectedEndYearMonth={selectionType === 'single' ? startYearMonth : endYearMonth}
            hoveredYearMonth={hoveredYearMonth}
            onDateSelect={this.handleYearMonthSelect}
            onMouseEnterMonth={this.handleMonthHover}
            onMouseLeaveMonth={this.handleMonthUnhover}
          />
        </CalendarYearWrapper>
        {calendarType === 'multiple' && (
          <CalendarYearWrapper>
            <CalendarTitleLayout layout="1fr auto">
              <Year>{displayedYear}</Year>
              <ArrowButton onClick={this.handleYearIncrease} data-test-id="year-next">
                <ArrowIcon icon="caret" size="small" />
              </ArrowButton>
            </CalendarTitleLayout>
            <MonthSelector
              year={displayedYear}
              selectedStartYearMonth={startYearMonth}
              selectRange={selectionType === 'range'}
              selectedEndYearMonth={selectionType === 'single' ? startYearMonth : endYearMonth}
              onDateSelect={this.handleYearMonthSelect}
              hoveredYearMonth={hoveredYearMonth}
              onMouseEnterMonth={this.handleMonthHover}
              onMouseLeaveMonth={this.handleMonthUnhover}
            />
          </CalendarYearWrapper>
        )}
      </Wrapper>
    );
  }
}

export default YearMonthRangePicker;
